/**
 * Frame upsell: opens before add-to-cart when enabled on product-form.
 */
if (!customElements.get('frame-upsell-modal')) {
  class FrameUpsellModal extends HTMLElement {
    constructor() {
      super();
      this.dialog = null;
      this.productForm = null;
      this.products = [];
      this.selectedProduct = null;
      this.selectedOptions = [];
      this.mainGallerySrcs = [];
      this.frameProductImages = [];
      this.framePreviewIndex = 0;
      this.productsRequest = null;
      this.boundDialogClose = this.onDialogClose.bind(this);
      this.boundKeydown = this.onDocumentKeydown.bind(this);
      this.boundOnHostClick = this.onHostClick.bind(this);
      this.boundDocumentPointerDownDismiss = this.onDocumentPointerDownDismiss.bind(this);
      this.boundPreviewPointerDown = this.onPreviewPointerDown.bind(this);
      this.boundPreviewDocumentPointerMove = this.onPreviewDocumentPointerMove.bind(this);
      this.boundPreviewDocumentPointerUp = this.onPreviewDocumentPointerUp.bind(this);
      this.previewGestureListenerOpts = { capture: true, passive: false };
      this.previewSwipePointerId = null;
      this.previewSwipeStartX = 0;
      this.previewSwipeStartY = 0;
      this.previewSwipeActive = false;
      this.previewGestureLock = null;
    }

    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      this.els = {
        subtitle: this.dialog.querySelector('[data-frame-upsell-subtitle]'),
        mainImg: this.dialog.querySelector('[data-frame-upsell-main-img]'),
        mainTitle: this.dialog.querySelector('[data-frame-upsell-main-title]'),
        descScroll: this.dialog.querySelector('[data-frame-upsell-desc-scroll]'),
        mainDesc: this.dialog.querySelector('[data-frame-upsell-main-desc]'),
        readMore: this.dialog.querySelector('[data-frame-upsell-read-more]'),
        swatches: this.dialog.querySelector('[data-frame-upsell-swatches]'),
        preview: this.dialog.querySelector('[data-frame-upsell-preview]'),
        matRow: this.dialog.querySelector('[data-frame-upsell-mat-row]'),
        matToggle: this.dialog.querySelector('[data-frame-upsell-mat]'),
        matPrice: this.dialog.querySelector('[data-frame-upsell-mat-price]'),
        framePrice: this.dialog.querySelector('[data-frame-upsell-frame-price]'),
        framePriceBtn: this.dialog.querySelector('[data-frame-upsell-btn-frame-price]'),
        variants: this.dialog.querySelector('[data-frame-upsell-variants]'),
        btnWith: this.dialog.querySelector('[data-frame-upsell-with-frame]'),
        btnSkip: this.dialog.querySelector('[data-frame-upsell-skip-frame]'),
        error: this.dialog.querySelector('[data-frame-upsell-error]'),
      };

      // Mockup mode: crop the poster region out of gray studio photos.
      this.mockupMode = this.dataset.mockupMode === 'true';

      this.matProducts = [];
      this.matForSize = null;
      this.matSelected = false;

      if (this.els.matToggle) {
        this.els.matToggle.addEventListener('change', () => {
          this.matSelected = this.els.matToggle.checked;
          this.renderCompositePreview();
          this.updateWithFrameButtonState();
        });
      }

      this.dialog.addEventListener('close', this.boundDialogClose);
      this.addEventListener('click', this.boundOnHostClick);

      this.previewSwipeZone = this.dialog.querySelector('.frame-upsell-modal__preview-inner');
      if (this.previewSwipeZone) {
        this.previewSwipeZone.addEventListener('pointerdown', this.boundPreviewPointerDown);
      }
    }

    disconnectedCallback() {
      this.dialog?.removeEventListener('close', this.boundDialogClose);
      document.removeEventListener('keydown', this.boundKeydown);
      this.disarmDesktopOutsideDismiss();
      this.removeEventListener('click', this.boundOnHostClick);
      this.disarmPreviewGestureDocumentListeners();
      if (this.previewSwipeZone) {
        this.previewSwipeZone.removeEventListener('pointerdown', this.boundPreviewPointerDown);
        this.previewSwipeZone = null;
      }
    }

    onDocumentKeydown(e) {
      if (e.key === 'Escape' && this.dialog?.open) {
        this.productForm = null;
      }
    }

    onDialogClose() {
      document.removeEventListener('keydown', this.boundKeydown);
      this.disarmDesktopOutsideDismiss();
      this.resetPreviewGestureState();
    }

    onHostClick(e) {
      const t = e.target;
      if (t.closest('[data-frame-upsell-read-more]')) {
        e.preventDefault();
        this.toggleReadMore();
      }
      if (t.closest('[data-frame-upsell-preview-prev]')) {
        e.preventDefault();
        e.stopPropagation();
        this.stepFramePreview(-1);
        return;
      }
      if (t.closest('[data-frame-upsell-preview-next]')) {
        e.preventDefault();
        e.stopPropagation();
        this.stepFramePreview(1);
        return;
      }
      const sw = t.closest('[data-frame-upsell-swatch]');
      if (sw && this.els.swatches?.contains(sw)) {
        e.preventDefault();
        const handle = sw.getAttribute('data-product-handle');
        const p = this.products.find((x) => x.handle === handle);
        if (p) this.selectFrameProduct(p);
      }
      if (t.closest('[data-frame-upsell-with-frame]')) {
        e.preventDefault();
        this.submitWithFrame();
      }
      if (t.closest('[data-frame-upsell-skip-frame]')) {
        e.preventDefault();
        this.submitSkipFrame();
      }
      if (t.closest('[data-frame-upsell-close]')) {
        e.preventDefault();
        if (this.isMobileViewport() && this.productForm) {
          this.submitSkipFrame();
          return;
        }
        this.close();
        this.productForm = null;
      }
    }

    toggleReadMore() {
      const wrap = this.els.descScroll;
      const btn = this.els.readMore;
      if (!wrap || !btn || btn.hidden) return;

      const expanding = !wrap.classList.contains('is-expanded');
      if (expanding) {
        wrap.classList.remove('is-collapsed');
        wrap.classList.add('is-expanded');
        btn.textContent = btn.dataset.less || 'Read less';
      } else {
        wrap.classList.remove('is-expanded');
        wrap.classList.add('is-collapsed');
        btn.textContent = btn.dataset.more || 'Read more';
      }
    }

    openForProductForm(productForm) {
      if (!this.dialog) return;
      this.productForm = productForm;
      this.mainProductOptions = this.getMainProductSelectedOptions();
      this.clearError();
      document.addEventListener('keydown', this.boundKeydown);
      this.syncMainProductFromDom();
      this.toggleReadMoreInit();

      const handle = this.dataset.collectionHandle;
      if (!handle) {
        this.showError('Frame collection is not configured.');
        return;
      }

      Promise.all([this.ensureProductsLoaded(handle), this.ensureMatsLoaded()])
        .then(() => {
          if (!this.products.length) {
            this.showError(this.dataset.emptyCollection || 'No products in collection.');
            return;
          }
          this.computeMatAvailability();
          this.renderSwatches();
          this.selectFrameProduct(this.products[0]);
          if (!this.dialog.open) this.dialog.showModal();
          this.armDesktopOutsideDismiss();
          this.trapFocus();
        })
        .catch(() => {
          this.showError(this.dataset.loadError || 'Could not load frames.');
        });
    }

    ensureMatsLoaded() {
      const handle = this.dataset.matCollectionHandle;
      if (!handle) return Promise.resolve();
      if (this.matProducts.length) return Promise.resolve();
      const url = `/collections/${encodeURIComponent(handle)}/products.json?limit=250`;
      return fetch(url)
        .then((r) => (r.ok ? r.json() : { products: [] }))
        .then((data) => {
          const re = /paspartu|passe-?partout|mat\b|kenarl[iı]k/i;
          this.matProducts = (data.products || [])
            .map((p) => {
              const optionNames = (p.options || [])
                .map((o) => (typeof o === 'string' ? o : o?.name || ''))
                .filter(Boolean);
              const variants = (p.variants || []).map((v) => {
                const opts =
                  v.options && v.options.length
                    ? v.options
                    : [v.option1, v.option2, v.option3].filter((x) => x != null && x !== '');
                return {
                  ...v,
                  options: opts,
                  price: this.toCents(v.price),
                  compare_at_price: this.toCents(v.compare_at_price),
                };
              });
              return { ...p, optionNames, variants };
            })
            .filter((p) => re.test(`${p.title || ''} ${p.handle || ''} ${p.product_type || ''} ${(p.tags || []).join(' ')}`));
        })
        .catch(() => {
          this.matProducts = [];
        });
    }

    getPosterSize() {
      const opts =
        this.mainProductOptions?.length > 0 ? this.mainProductOptions : this.getMainProductSelectedOptions();
      for (const val of opts) {
        if (window.YarisFramePreview?.parseSize(val)) return val;
      }
      return opts[0] || null;
    }

    computeMatAvailability() {
      this.matForSize = null;
      const FP = window.YarisFramePreview;
      if (!FP || !this.matProducts.length) return;
      const size = this.getPosterSize();
      const matSize = size ? FP.nextSizeUp(size) : null;
      if (!matSize) return;
      for (const product of this.matProducts) {
        const match = product.variants.find(
          (v) => v.available !== false && v.options.some((o) => FP.sizeValuesMatch(o, matSize))
        );
        if (match) {
          this.matForSize = { product, variant: match };
          break;
        }
      }
      if (this.els.matRow) this.els.matRow.hidden = !this.matForSize;
      if (this.matForSize && this.els.matPrice) {
        const price = Number(this.matForSize.variant.price);
        this.els.matPrice.textContent = price > 0 ? `+${this.formatMoney(price)}` : '';
        this.els.matPrice.hidden = !(price > 0);
      }
      if (!this.matForSize) {
        this.matSelected = false;
        if (this.els.matToggle) this.els.matToggle.checked = false;
      }
    }

    /** Colour name of the currently selected frame variant/product. */
    getSelectedFrameColorName() {
      const product = this.selectedProduct;
      if (!product) return '';
      const names = product.optionNames || product.options || [];
      const variant = this.getSelectedFrameVariant();
      for (let i = 0; i < names.length; i += 1) {
        const label = typeof names[i] === 'string' ? names[i] : names[i]?.name || '';
        if (/renk|color|colour/i.test(label)) {
          return (variant?.options?.[i]) || this.selectedOptions[i] || '';
        }
      }
      return product.title || '';
    }

    renderCompositePreview() {
      const FP = window.YarisFramePreview;
      if (!FP || !this.els.preview) return;
      const posterSrc = this.mainGallerySrcs?.[0] || this.els.mainImg?.src || '';
      const token = FP.resolveFrameToken(this.getSelectedFrameColorName());
      const mat = Boolean(this.matSelected && this.matForSize);

      // Crop only in mockup mode; edge-to-edge images are framed whole.
      let crop = null;
      if (this.mockupMode && posterSrc) {
        crop = FP.cachedContentBounds({ src: posterSrc });
        if (crop === undefined) {
          crop = null;
          FP.getContentBounds({ src: posterSrc }).then(() => {
            const current = this.mainGallerySrcs?.[0] || this.els.mainImg?.src || '';
            if (current === posterSrc) this.renderCompositePreview();
          });
        }
      }

      const probe = this.getPreviewProbe(posterSrc);
      let ratio = probe && probe.naturalWidth ? FP.imageRatio(probe, crop) : null;
      // A crop needs a locked window ratio; wait for natural size if unknown.
      if (crop && !ratio) crop = null;

      const opts = { frameToken: token, imgSrc: posterSrc, mat, alt: '', ratio, crop };
      if (!this.compositeEl) {
        this.compositeEl = FP.buildPreview(opts);
        this.els.preview.innerHTML = '';
        this.els.preview.appendChild(this.compositeEl);
      } else {
        FP.updatePreview(this.compositeEl, opts);
      }
    }

    /** Detached image used to read the poster's natural size for the ratio. */
    getPreviewProbe(src) {
      if (!src) return null;
      if (!this._probe || this._probeSrc !== src) {
        this._probe = new Image();
        this._probeSrc = src;
        this._probe.onload = () => {
          const current = this.mainGallerySrcs?.[0] || this.els.mainImg?.src || '';
          if (current === this._probeSrc) this.renderCompositePreview();
        };
        this._probe.src = src;
      }
      return this._probe;
    }

    trapFocus() {
      requestAnimationFrame(() => {
        if (this.isDesktopViewport()) {
          const firstSwatch = this.dialog?.querySelector('[data-frame-upsell-swatch]');
          const primary = this.els?.btnWith;
          const target = firstSwatch || primary;
          if (target && typeof target.focus === 'function') {
            target.focus();
            return;
          }
        }
        const closeBtn = this.dialog.querySelector('[data-frame-upsell-close]');
        closeBtn?.focus();
      });
    }

    armDesktopOutsideDismiss() {
      if (!this.isDesktopViewport()) return;
      document.removeEventListener('pointerdown', this.boundDocumentPointerDownDismiss, true);
      document.addEventListener('pointerdown', this.boundDocumentPointerDownDismiss, true);
    }

    disarmDesktopOutsideDismiss() {
      document.removeEventListener('pointerdown', this.boundDocumentPointerDownDismiss, true);
    }

    onDocumentPointerDownDismiss(e) {
      if (!this.dialog?.open || !this.isDesktopViewport()) return;
      const rect = this.dialog.getBoundingClientRect();
      const { clientX: x, clientY: y } = e;
      const outside = x < rect.left || x > rect.right || y < rect.top || y > rect.bottom;
      if (!outside) return;
      e.preventDefault();
      if (this.productForm) {
        this.submitSkipFrame();
        return;
      }
      this.close();
      this.productForm = null;
    }

    toggleReadMoreInit() {
      const wrap = this.els.descScroll;
      const btn = this.els.readMore;
      if (!btn) return;
      wrap?.classList.remove('is-expanded');
      wrap?.classList.add('is-collapsed');
      btn.textContent = btn.dataset.more || 'Read more';
      btn.hidden = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.updateReadMoreVisibility());
      });
    }

    updateReadMoreVisibility() {
      const wrap = this.els.descScroll;
      const inner = this.els.mainDesc;
      const btn = this.els.readMore;
      if (!wrap || !inner || !btn) return;

      wrap.classList.remove('is-expanded');
      wrap.classList.add('is-collapsed');

      const overflow = inner.scrollHeight > wrap.clientHeight + 2;
      if (overflow) {
        btn.hidden = false;
      } else {
        wrap.classList.remove('is-collapsed');
        btn.hidden = true;
      }
    }

    isMobileViewport() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    isDesktopViewport() {
      return window.matchMedia('(min-width: 750px)').matches;
    }

    close() {
      const wrap = this.els.descScroll;
      if (wrap) {
        wrap.classList.remove('is-expanded');
        wrap.classList.remove('is-collapsed');
      }
      this.dialog?.close();
    }

    clearError() {
      if (this.els.error) {
        this.els.error.textContent = '';
        this.els.error.hidden = true;
      }
    }

    showError(msg) {
      if (!this.els.error) return;
      this.els.error.textContent = msg;
      this.els.error.hidden = false;
    }

    ensureProductsLoaded(handle) {
      if (this.products.length && this.dataset.collectionHandle === handle) {
        return Promise.resolve();
      }
      if (this.productsRequest) return this.productsRequest;

      const url = `/collections/${encodeURIComponent(handle)}/products.json?limit=50`;
      this.productsRequest = fetch(url)
        .then((r) => {
          if (!r.ok) throw new Error('bad response');
          return r.json();
        })
        .then((data) => {
          this.products = (data.products || []).map((p) => {
            const optionNames = (p.options || [])
              .map((o) => (typeof o === 'string' ? o : o?.name || ''))
              .filter(Boolean);
            const variants = (p.variants || []).map((v) => {
              const opts =
                v.options && v.options.length
                  ? v.options
                  : [v.option1, v.option2, v.option3].filter((x) => x != null && x !== '');
              return {
                ...v,
                options: opts,
                price: this.toCents(v.price),
                compare_at_price: this.toCents(v.compare_at_price),
              };
            });
            return { ...p, optionNames, variants };
          });
          this.productsRequest = null;
        })
        .catch((e) => {
          this.productsRequest = null;
          throw e;
        });

      return this.productsRequest;
    }

    syncMainProductFromDom() {
      const sectionId = this.dataset.sectionId;
      const root = document.querySelector(`#MainProduct-${sectionId}`);
      const galleryId = this.dataset.galleryRootId;
      const gallery = galleryId ? document.getElementById(galleryId) : null;

      this.mainGallerySrcs = [];
      if (gallery) {
        const imgs = gallery.querySelectorAll('.product__media-item img');
        for (const img of imgs) {
          const src = img.currentSrc || img.src;
          if (src && !this.mainGallerySrcs.includes(src)) this.mainGallerySrcs.push(src);
        }
      }
      if (this.mainGallerySrcs.length === 0 && root) {
        const fallback = root.querySelector('.product__media-item.is-active img, .product__media img');
        if (fallback) this.mainGallerySrcs.push(fallback.currentSrc || fallback.src);
      }
      if (this.els.mainImg && this.mainGallerySrcs[0]) {
        this.els.mainImg.src = this.mainGallerySrcs[0];
        this.els.mainImg.alt = '';
      }

      if (this.els.mainTitle) {
        const h1 = root?.querySelector('h1.product__heading, .product__title h1');
        if (h1?.textContent?.trim()) {
          this.els.mainTitle.textContent = h1.textContent.trim();
        } else if (this.dataset.productTitle) {
          this.els.mainTitle.textContent = this.dataset.productTitle;
        } else {
          this.els.mainTitle.textContent = '';
        }
      }
      if (root && this.els.mainDesc) {
        const desc = root.querySelector('.product__description .rte, .product__description');
        this.els.mainDesc.innerHTML = desc?.innerHTML || '';
      }
    }

    productPreviewSrc(product) {
      if (!product) return '';
      if (product.featured_image) {
        if (typeof product.featured_image === 'string') return product.featured_image;
        return product.featured_image.src || '';
      }
      if (product.image) {
        if (typeof product.image === 'string') return product.image;
        return product.image.src || '';
      }
      if (product.images?.[0]) {
        const im = product.images[0];
        return typeof im === 'string' ? im : im.src || '';
      }
      const v = product.variants?.[0];
      if (v?.featured_image) return v.featured_image.src || '';
      return '';
    }

    collectFrameProductImageUrls(product) {
      if (!product) return [];
      const list = [];
      const seen = new Set();
      const push = (url) => {
        if (!url || typeof url !== 'string') return;
        const key = this.normalizeImageUrl(url);
        if (!key || seen.has(key)) return;
        seen.add(key);
        list.push(url);
      };
      if (Array.isArray(product.images) && product.images.length) {
        const sorted = [...product.images].sort((a, b) => {
          const pa = typeof a === 'object' && a != null ? Number(a.position) || 0 : 0;
          const pb = typeof b === 'object' && b != null ? Number(b.position) || 0 : 0;
          return pa - pb;
        });
        for (const im of sorted) {
          const src = typeof im === 'string' ? im : im?.src;
          push(src);
        }
      }
      if (list.length === 0) {
        push(this.productPreviewSrc(product));
      }
      return list;
    }

    normalizeImageUrl(url) {
      if (!url || typeof url !== 'string') return '';
      try {
        const u = new URL(url, window.location.origin);
        return u.pathname;
      } catch {
        return url.split('?')[0];
      }
    }

    getImageIndexForVariant(variant) {
      if (!variant?.featured_image?.src || !this.frameProductImages.length) return 0;
      const target = this.normalizeImageUrl(variant.featured_image.src);
      const idx = this.frameProductImages.findIndex(
        (u) => this.normalizeImageUrl(u) === target
      );
      return idx >= 0 ? idx : 0;
    }

    rebuildFramePreviewGallery(product, variantForIndex) {
      this.frameProductImages = this.collectFrameProductImageUrls(product);
      this.framePreviewIndex = this.getImageIndexForVariant(variantForIndex);
      this.preloadFramePreviewUrls(this.frameProductImages);
      this.applyFramePreviewIndex();
    }

    preloadFramePreviewUrls(urls) {
      if (!urls?.length) return;
      const seen = new Set();
      for (const url of urls) {
        if (!url || typeof url !== 'string' || seen.has(url)) continue;
        seen.add(url);
        const pre = new Image();
        pre.src = url;
      }
    }

    applyFramePreviewIndex() {
      const urls = this.frameProductImages;
      const img = this.els.previewImg;
      if (!img) return;
      if (!urls.length) {
        img.removeAttribute('src');
        img.alt = '';
        if (this.els.previewControls) this.els.previewControls.hidden = true;
        if (this.previewSwipeZone) {
          this.previewSwipeZone.classList.remove('is-frame-preview-swipeable', 'is-preview-swiping');
        }
        return;
      }
      const max = urls.length - 1;
      this.framePreviewIndex = Math.max(0, Math.min(this.framePreviewIndex, max));
      if ('fetchPriority' in img) img.fetchPriority = 'high';
      img.src = urls[this.framePreviewIndex];
      const title = this.selectedProduct?.title || '';
      img.alt =
        urls.length > 1
          ? `${title} (${this.framePreviewIndex + 1} / ${urls.length})`
          : title;
      const showNav = urls.length > 1;
      if (this.els.previewControls) this.els.previewControls.hidden = !showNav;
      if (this.previewSwipeZone) {
        this.previewSwipeZone.classList.toggle('is-frame-preview-swipeable', showNav);
      }
    }

    stepFramePreview(delta) {
      const urls = this.frameProductImages;
      if (!urls || urls.length < 2) return;
      this.framePreviewIndex = (this.framePreviewIndex + delta + urls.length) % urls.length;
      this.applyFramePreviewIndex();
    }

    previewSwipeHasMultipleImages() {
      return Boolean(this.frameProductImages && this.frameProductImages.length > 1);
    }

    onPreviewPointerDown(e) {
      if (!this.previewSwipeHasMultipleImages()) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.previewSwipeActive = true;
      this.previewSwipePointerId = e.pointerId;
      this.previewSwipeStartX = e.clientX;
      this.previewSwipeStartY = e.clientY;
      this.previewGestureLock = null;
      document.addEventListener(
        'pointermove',
        this.boundPreviewDocumentPointerMove,
        this.previewGestureListenerOpts
      );
      document.addEventListener(
        'pointerup',
        this.boundPreviewDocumentPointerUp,
        this.previewGestureListenerOpts
      );
      document.addEventListener(
        'pointercancel',
        this.boundPreviewDocumentPointerUp,
        this.previewGestureListenerOpts
      );
    }

    onPreviewDocumentPointerMove(e) {
      if (!this.previewSwipeActive || e.pointerId !== this.previewSwipePointerId) return;
      const dx = e.clientX - this.previewSwipeStartX;
      const dy = e.clientY - this.previewSwipeStartY;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      if (this.previewGestureLock === null) {
        if (absDx < 10 && absDy < 10) return;
        if (absDx >= absDy * 1.15 && absDx >= 10) {
          this.previewGestureLock = 'horizontal';
          this.previewSwipeZone?.classList.add('is-preview-swiping');
        } else if (absDy >= absDx * 1.15 && absDy >= 10) {
          this.previewGestureLock = 'vertical';
          return;
        } else {
          return;
        }
      }

      if (this.previewGestureLock === 'horizontal' && e.pointerType !== 'mouse') {
        e.preventDefault();
      }
    }

    onPreviewDocumentPointerUp(e) {
      if (!this.previewSwipeActive || e.pointerId !== this.previewSwipePointerId) return;
      const dx = e.clientX - this.previewSwipeStartX;
      const dy = e.clientY - this.previewSwipeStartY;
      this.disarmPreviewGestureDocumentListeners();
      this.previewSwipeZone?.classList.remove('is-preview-swiping');
      this.previewSwipeActive = false;
      this.previewSwipePointerId = null;
      const lock = this.previewGestureLock;
      this.previewGestureLock = null;

      if (lock !== 'horizontal') return;
      const minPx = 48;
      if (Math.abs(dx) < minPx || Math.abs(dx) < Math.abs(dy) * 1.15) return;
      if (dx < 0) {
        this.stepFramePreview(1);
      } else {
        this.stepFramePreview(-1);
      }
    }

    disarmPreviewGestureDocumentListeners() {
      document.removeEventListener(
        'pointermove',
        this.boundPreviewDocumentPointerMove,
        this.previewGestureListenerOpts
      );
      document.removeEventListener(
        'pointerup',
        this.boundPreviewDocumentPointerUp,
        this.previewGestureListenerOpts
      );
      document.removeEventListener(
        'pointercancel',
        this.boundPreviewDocumentPointerUp,
        this.previewGestureListenerOpts
      );
    }

    resetPreviewGestureState() {
      this.disarmPreviewGestureDocumentListeners();
      this.previewSwipeActive = false;
      this.previewSwipePointerId = null;
      this.previewGestureLock = null;
      this.previewSwipeZone?.classList.remove('is-preview-swiping');
    }

    /** Convert a /products.json price (major units string) to integer cents. */
    toCents(value) {
      if (value == null || value === '') return null;
      const n = parseFloat(value);
      return Number.isFinite(n) ? Math.round(n * 100) : null;
    }

    formatMoney(cents) {
      if (window.YarisMoney) return window.YarisMoney.format(cents);
      return (cents / 100).toFixed(2);
    }

    renderSwatches() {
      if (!this.els.swatches) return;
      this.els.swatches.innerHTML = '';
      for (const p of this.products) {
        const src = this.productPreviewSrc(p);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'frame-upsell-modal__swatch';
        btn.setAttribute('data-frame-upsell-swatch', '');
        btn.setAttribute('data-product-handle', p.handle);
        btn.innerHTML = `
          <img src="${src}" alt="" loading="lazy" width="120" height="120" />
          <span class="frame-upsell-modal__swatch-title">${this.escapeHtml(p.title)}</span>
        `;
        this.els.swatches.appendChild(btn);
      }
    }

    escapeHtml(str) {
      const d = document.createElement('div');
      d.textContent = str;
      return d.innerHTML;
    }

    getMainProductRoot() {
      const sectionId = this.dataset.sectionId;
      return document.querySelector(`#MainProduct-${sectionId}`);
    }

    readOptionValueFromPickerChild(el) {
      if (!el || el.tagName === 'SCRIPT') return null;

      const sheet =
        el.tagName === 'SIZE-OPTION-SHEET' ? el : el.querySelector('size-option-sheet');
      if (sheet) {
        const select = sheet.querySelector('select');
        const selected = select?.options?.[select.selectedIndex];
        if (select?.value && selected?.dataset?.placeholder !== 'true') {
          return select.value;
        }

        const triggerValue = sheet.querySelector('[data-size-value]')?.getAttribute('data-size-value');
        if (triggerValue) return triggerValue;

        const selectedOption = sheet.querySelector('.size-option-sheet__option[aria-selected="true"]');
        return selectedOption?.getAttribute('data-value') || null;
      }

      if (el.matches('fieldset')) {
        return el.querySelector('input[type="radio"]:checked')?.value || null;
      }

      const select = el.querySelector('select[name^="options["]');
      if (select?.value) {
        const selected = select.options[select.selectedIndex];
        if (selected?.dataset?.placeholder === 'true') return null;
        return select.value;
      }

      return null;
    }

    getMainProductSelectedOptionsFromJson() {
      const variantSelects = this.getMainProductRoot()?.querySelector('variant-selects[data-primary]');
      const jsonEl = variantSelects?.querySelector('[data-selected-variant]');
      if (!jsonEl?.textContent?.trim()) return [];

      try {
        const variant = JSON.parse(jsonEl.textContent);
        if (Array.isArray(variant?.options) && variant.options.length) {
          return variant.options.filter((value) => value != null && value !== '');
        }
        return [variant.option1, variant.option2, variant.option3].filter(
          (value) => value != null && value !== ''
        );
      } catch {
        return [];
      }
    }

    getMainProductSelectedOptionsFromVariantId() {
      const variantId =
        this.productForm?.querySelector('input[name="id"]')?.value ||
        this.productForm?.variantIdInput?.value;
      if (!variantId) return [];

      const root = this.getMainProductRoot();
      const variantsEl = root?.querySelector('[data-size-sheet-variants]');
      if (!variantsEl?.textContent?.trim()) return [];

      try {
        const variants = JSON.parse(variantsEl.textContent);
        const variant = variants.find((entry) => String(entry.id) === String(variantId));
        if (!variant) return [];

        return [variant.option1, variant.option2, variant.option3].filter(
          (value) => value != null && value !== ''
        );
      } catch {
        return [];
      }
    }

    getMainProductSelectedOptions() {
      const sectionId = this.dataset.sectionId;
      const variantSelects = this.getMainProductRoot()?.querySelector('variant-selects[data-primary]');
      const values = [];

      if (variantSelects) {
        for (const child of variantSelects.children) {
          const value = this.readOptionValueFromPickerChild(child);
          if (value) values.push(value);
        }
      }

      if (!values.length) {
        for (let index = 0; index < 3; index += 1) {
          const select = document.getElementById(`Option-${sectionId}-${index}`);
          if (!select?.value) continue;
          const selected = select.options[select.selectedIndex];
          if (selected?.dataset?.placeholder === 'true') continue;
          values.push(select.value);
        }
      }

      if (values.length) return values;

      const fromVariantId = this.getMainProductSelectedOptionsFromVariantId();
      if (fromVariantId.length) return fromVariantId;

      return this.getMainProductSelectedOptionsFromJson();
    }

    looksLikeDimensionValue(value) {
      if (!value) return false;
      if (window.YarisSizeUnits?.parseSizeValue?.(value)) return true;
      return /\d+\s*[x×X]\s*\d+/i.test(String(value));
    }

    normalizeOptionValue(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
    }

    optionValuesLooselyMatch(mainValue, frameValue) {
      if (!mainValue || !frameValue) return false;

      const mainNorm = this.normalizeOptionValue(mainValue);
      const frameNorm = this.normalizeOptionValue(frameValue);
      if (mainNorm === frameNorm) return true;

      const units = window.YarisSizeUnits;
      if (units?.parseSizeValue) {
        const parsedMain = units.parseSizeValue(mainValue);
        const parsedFrame = units.parseSizeValue(frameValue);
        if (parsedMain && parsedFrame) {
          if (parsedMain.type === 'dim' && parsedFrame.type === 'dim') {
            return Math.abs(parsedMain.w - parsedFrame.w) < 1 && Math.abs(parsedMain.h - parsedFrame.h) < 1;
          }
          if (parsedMain.type === 'a' && parsedFrame.type === 'a') {
            return parsedMain.label === parsedFrame.label;
          }
        }
      }

      const stripUnits = (str) =>
        String(str || '')
          .trim()
          .toLowerCase()
          .replace(/\s*(cm|in|inch|inç|")\s*/gi, '')
          .replace(/[×x]/g, 'x')
          .replace(/\s+/g, '');

      return stripUnits(mainValue) === stripUnits(frameValue);
    }

    scoreFrameVariantAgainstMainOptions(variant, mainOptions) {
      return mainOptions.reduce(
        (score, mainValue) =>
          score +
          (variant.options.some((optionValue) => this.optionValuesLooselyMatch(mainValue, optionValue))
            ? 1
            : 0),
        0
      );
    }

    findFrameVariantMatchingMainOptions(mainOptions) {
      if (!this.selectedProduct?.variants?.length || !mainOptions?.length) return null;

      const available = this.selectedProduct.variants.filter((variant) => variant.available !== false);
      if (!available.length) return null;

      const dimensionValues = mainOptions.filter((value) => this.looksLikeDimensionValue(value));
      const priorityValues = dimensionValues.length ? dimensionValues : mainOptions;
      let candidates = [];

      for (const mainValue of priorityValues) {
        const matches = available.filter((variant) =>
          variant.options.some((optionValue) => this.optionValuesLooselyMatch(mainValue, optionValue))
        );
        if (matches.length) {
          candidates = matches;
          break;
        }
      }

      if (!candidates.length) return null;

      return candidates.sort(
        (a, b) => this.scoreFrameVariantAgainstMainOptions(b, mainOptions) -
          this.scoreFrameVariantAgainstMainOptions(a, mainOptions)
      )[0];
    }

    applyMainProductOptionsToFrame(mainOptions) {
      const product = this.selectedProduct;
      const names = product.optionNames || product.options || [];
      if (!product || !names.length || !mainOptions?.length) return;

      const matchedVariant = this.findFrameVariantMatchingMainOptions(mainOptions);
      if (!matchedVariant) return;

      this.selectedOptions = [...matchedVariant.options];
      while (this.selectedOptions.length < names.length) {
        this.selectedOptions.push(null);
      }
    }

    selectFrameProduct(product) {
      this.selectedProduct = product;
      this.els.swatches?.querySelectorAll('.frame-upsell-modal__swatch').forEach((el) => {
        el.classList.toggle('is-selected', el.getAttribute('data-product-handle') === product.handle);
      });

      const firstAvail = product.variants.find((v) => v.available) || product.variants[0];
      this.selectedOptions = firstAvail ? [...firstAvail.options] : [];
      while (this.selectedOptions.length < (product.optionNames?.length || 0)) {
        this.selectedOptions.push(null);
      }

      const mainOptions =
        this.mainProductOptions?.length > 0
          ? this.mainProductOptions
          : this.getMainProductSelectedOptions();
      if (mainOptions.length) {
        this.applyMainProductOptionsToFrame(mainOptions);
      }

      this.renderCompositePreview();

      this.renderFrameVariants();
    }

    normalizeSelections() {
      const product = this.selectedProduct;
      const len = (product.optionNames || product.options || []).length;
      for (let i = 0; i < len; i++) {
        const vals = this.optionValuesForIndex(i);
        if (!vals.length) continue;
        if (!vals.includes(this.selectedOptions[i])) {
          this.selectedOptions[i] = vals[0];
        }
      }
    }

    optionValuesForIndex(optionIndex) {
      const product = this.selectedProduct;
      const names = product.optionNames || product.options || [];
      if (!names[optionIndex]) return [];

      const values = [];
      const seen = new Set();
      for (const v of product.variants) {
        let match = true;
        for (let i = 0; i < optionIndex; i++) {
          if (v.options[i] !== this.selectedOptions[i]) {
            match = false;
            break;
          }
        }
        if (!match) continue;
        if (!v.available) continue;
        const val = v.options[optionIndex];
        if (val == null || val === '') continue;
        if (!seen.has(val)) {
          seen.add(val);
          values.push(val);
        }
      }
      return values;
    }

    renderFrameVariants() {
      if (!this.els.variants || !this.selectedProduct) return;
      this.els.variants.innerHTML = '';
      const product = this.selectedProduct;
      const names = product.optionNames || product.options || [];
      if (!names.length || product.variants.length <= 1) {
        this.els.variants.hidden = true;
        this.updatePreviewForVariant();
        this.updateFramePrice();
        this.updateWithFrameButtonState();
        return;
      }
      this.els.variants.hidden = false;
      this.normalizeSelections();

      names.forEach((optionName, optionIndex) => {
        const values = this.optionValuesForIndex(optionIndex);
        if (!values.length) return;

        const wrap = document.createElement('div');
        wrap.className = 'frame-upsell-modal__variant-group';

        const title = document.createElement('p');
        title.className = 'frame-upsell-modal__option-name';
        title.textContent =
          typeof optionName === 'string' ? optionName : optionName?.name || '';

        const row = document.createElement('div');
        row.className = 'frame-upsell-modal__option-values';

        for (const val of values) {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'frame-upsell-modal__option-btn';
          b.setAttribute('data-option-index', String(optionIndex));
          b.setAttribute('data-option-value', val);

          const optionLabel = typeof optionName === 'string' ? optionName : optionName?.name || '';
          if (window.YarisSizeUnits?.isDimensionOptionName(optionLabel)) {
            b.setAttribute('data-size-value', val);
            const span = document.createElement('span');
            span.setAttribute('data-size-display', '');
            span.textContent = val;
            b.appendChild(span);
          } else {
            b.textContent = val;
          }

          if (this.selectedOptions[optionIndex] === val) b.classList.add('is-selected');
          b.addEventListener('click', () => {
            this.selectedOptions[optionIndex] = val;
            this.cascadeSelections(optionIndex);
            this.renderFrameVariants();
          });
          row.appendChild(b);
        }

        wrap.appendChild(title);
        wrap.appendChild(row);
        this.els.variants.appendChild(wrap);
      });

      this.updatePreviewForVariant();
      this.updateFramePrice();
      this.updateWithFrameButtonState();
      window.YarisSizeUnits?.applyAll?.();
    }

    cascadeSelections(fromIndex) {
      const product = this.selectedProduct;
      const names = product.optionNames || product.options || [];
      for (let j = fromIndex + 1; j < names.length; j++) {
        const vals = this.optionValuesForIndex(j);
        if (vals.includes(this.selectedOptions[j])) continue;
        this.selectedOptions[j] = vals[0] || null;
      }
    }

    getSelectedFrameVariant() {
      if (!this.selectedProduct?.variants?.length) return null;
      const variants = this.selectedProduct.variants;
      if (variants.length === 1) {
        return variants[0].available ? variants[0] : null;
      }
      const names = this.selectedProduct.optionNames || this.selectedProduct.options || [];
      if (!names.length) {
        return variants.find((v) => v.available) || null;
      }
      return variants.find(
        (v) =>
          v.available &&
          v.options.length === names.length &&
          v.options.every((opt, i) => opt === this.selectedOptions[i])
      );
    }

    updatePreviewForVariant() {
      this.renderCompositePreview();
    }

    updateFramePrice() {
      if (!this.els.framePrice && !this.els.framePriceBtn) return;
      const v = this.getSelectedFrameVariant();
      const nodes = [this.els.framePrice, this.els.framePriceBtn].filter(Boolean);
      if (!v) {
        for (const el of nodes) {
          el.innerHTML = '';
          el.hidden = true;
          el.classList.remove('is-on-sale');
        }
        return;
      }
      const cmp = Number(v.compare_at_price);
      const price = Number(v.price);
      const onSale = Boolean(cmp && cmp > price);
      const fullHtml = onSale
        ? `<span class="frame-upsell-modal__frame-price-sale">${this.formatMoney(v.price)}</span> <s class="frame-upsell-modal__frame-price-compare">${this.formatMoney(v.compare_at_price)}</s>`
        : '';
      const plain = onSale ? '' : this.formatMoney(v.price);
      for (const el of nodes) {
        if (onSale) {
          el.classList.add('is-on-sale');
          el.innerHTML = fullHtml;
        } else {
          el.classList.remove('is-on-sale');
          el.textContent = plain;
        }
        el.hidden = false;
      }
    }

    updateWithFrameButtonState() {
      const v = this.getSelectedFrameVariant();
      if (this.els.btnWith) {
        this.els.btnWith.disabled = !v;
      }
    }

    submitSkipFrame() {
      if (!this.productForm) return;
      this.close();
      this.productForm.dataset.ignoreFrameUpsellOnce = 'true';
      this.productForm.form.requestSubmit();
    }

    submitWithFrame() {
      const frameVariant = this.getSelectedFrameVariant();
      if (!this.productForm || !frameVariant) return;
      this.clearError();
      const ids = [frameVariant.id];
      if (this.matSelected && this.matForSize?.variant) ids.push(this.matForSize.variant.id);
      if (typeof this.productForm.addToCartWithAddons === 'function') {
        this.productForm.addToCartWithAddons(ids, () => this.close());
      } else {
        this.productForm.addToCartWithFrameVariant?.(frameVariant.id, () => this.close());
      }
    }
  }

  customElements.define('frame-upsell-modal', FrameUpsellModal);
}
